var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { Tab } from '@headlessui/react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../Icon/index.js';
import { TabsProvider, useTabs } from './TabsContext.js';
export const Tabs = () => null;
const TabGroup = props => {
    const { as: Component = Tab.Group, children } = props, rest = __rest(props, ["as", "children"]);
    return React.createElement(Component, Object.assign({}, rest), children);
};
const TabList = props => {
    const { as: Component = Tab.List, children, className, variant = 'bar', size = 'md', fullWidth = false, shadow = true, rounded = 'desktop', divide = true, attached = 'none', } = props;
    const barClsx = [
        'z-0',
        divide && 'divide-x divide-line-subdued',
        shadow && 'shadow',
        rounded === 'none' && 'rounded-t-none rounded-b-none',
        rounded === 'desktop' &&
            'rounded-t-none rounded-b-none sm:rounded-t-lg sm:rounded-b-lg',
        rounded === 'all' && 'rounded-t-lg rounded-b-lg',
    ];
    let attachedClsx = '';
    if (attached === 'top') {
        attachedClsx =
            'rounded-t-none sm:rounded-t-none border-t border-t-line-subdued';
    }
    if (attached === 'bottom') {
        attachedClsx =
            'rounded-b-none sm:rounded-b-none border-b border-b-line-subdued';
    }
    return (React.createElement(TabsProvider, { variant: variant, size: size, fullWidth: fullWidth },
        React.createElement(Component, { className: twMerge(clsx('overflow-x-auto isolate relative', variant === 'bar' && barClsx, variant === 'bar' && attachedClsx, variant === 'pills' && 'space-s-4 mx-2 sm:mx-0', variant === 'pills-accent' && 'space-s-4 mx-2 sm:mx-0', !fullWidth && 'inline-flex flex-nowrap', fullWidth && 'flex  flex-nowrap', className)), "aria-label": "Tabs" }, ({ selectedIndex }) => {
            return React.Children.toArray(children).map((child, index) => {
                if (React.isValidElement(child)) {
                    return React.cloneElement(child, {
                        selected: index === selectedIndex,
                    });
                }
                return child;
            });
        })));
};
const TabItem = props => {
    const { as: Component = Tab, children, leadingIcon, className: customClassName, disabled, selected: customSelected } = props, rest = __rest(props, ["as", "children", "leadingIcon", "className", "disabled", "selected"]);
    const { variant, size, fullWidth } = useTabs();
    const variantBar = selected => [
        selected
            ? 'text-content'
            : 'text-content-subdued hover:text-content-hovered',
        'group relative text-center',
        'bg-surface  hover:bg-surface-hovered',
        size === 'md' && 'py-4 px-4 m-0 text-sm',
        size === 'sm' && 'py-3 px-2 m-0 text-sm leading-4',
    ];
    const variantPills = selected => [
        selected
            ? 'bg-surface-pressed text-content-subdued'
            : 'text-content-subdued hover:text-content-hovered',
        'rounded-md',
        size === 'md' && 'px-3 py-1.5',
        size === 'sm' && 'px-2 py-1.5 text-sm leading-4',
        'focus-visible:ring ring-inset ring-offset-0',
    ];
    const variantPillsAccent = selected => [
        selected
            ? 'bg-action-primary-subdued text-content-primary'
            : 'text-content-subdued hover:text-content-hovered',
        'rounded-md',
        size === 'md' && 'px-3 py-1.5',
        size === 'sm' && 'px-2 py-1.5 text-sm leading-4',
        'focus-visible:ring ring-inset ring-offset-0',
    ];
    let className = ({ selected }) => clsx(!fullWidth && 'flex-grow-0 flex-shrink-0', fullWidth && 'flex-1', variant === 'bar' && variantBar(selected), variant === 'pills' && variantPills(selected), variant === 'pills-accent' && variantPillsAccent(selected), 'focus:outline-none focus:z-10', disabled && 'opacity-50 cursor-default', customClassName);
    let content = ({ selected }) => (React.createElement(React.Fragment, null,
        leadingIcon && (React.createElement(Icon, { className: clsx(selected
                ? 'text-action-primary'
                : 'text-content-subdued group-hover:text-content-hovered', 'inline-block -ms-0.5 me-2 h-5 w-5') }, leadingIcon)),
        children,
        variant === 'bar' && (React.createElement("span", { "aria-hidden": "true", className: clsx(selected ? 'bg-action-primary' : 'bg-transparent', 'absolute inset-x-0 bottom-0 h-0.5') }))));
    if (customSelected !== undefined) {
        className = className({ selected: customSelected });
        content = content({ selected: customSelected });
    }
    return (React.createElement(Component, Object.assign({ className: className, disabled: disabled }, rest), content));
};
const TabPanels = props => {
    const { as: Component = Tab.Panels, spacing = 'md', children, className } = props, rest = __rest(props, ["as", "spacing", "children", "className"]);
    return (React.createElement("div", Object.assign({ className: clsx([
            spacing === 'lg' && 'mt-5 sm:mt-6 md:mt-7 lg:mt-8',
            spacing === 'md' && 'mt-3 sm:mt-3.5 md:mt-4 lg:mt-5',
            spacing === 'sm' && 'mt-2 sm:mt-2.5 lg:mt-3',
            spacing === 'none' && 'mt-0',
        ], className) }, rest),
        React.createElement(Component, null, ({ selectedIndex }) => {
            return React.Children.toArray(children).map((child, index) => {
                if (index !== selectedIndex) {
                    return null;
                }
                return child;
            });
        })));
};
const TabPanel = props => {
    const { children, className } = props, rest = __rest(props, ["children", "className"]);
    return (React.createElement(Tab.Panel, Object.assign({ className: clsx('focus:outline-none focus-visible:ring', className) }, rest, { static: true }), children));
};
Tabs.Group = TabGroup;
Tabs.List = TabList;
Tabs.Tab = TabItem;
Tabs.Panels = TabPanels;
Tabs.Panel = TabPanel;
//# sourceMappingURL=Tabs.js.map